<template>
  <div id="app" :class="{ showSideBar }">
    <Login
      @loginSuccessHandler="loginSuccessHandler"
      @loginFailHandler="loginFailHandler"
      @loginNewUserHandler="loginNewUserHandler"
    />
    <Header />
    <SideBar />
    <div class="workspace-area">
      <BlockArea />
      <CodeArea />
    </div>
    <LanguagePopup />
    <UserPopup />
    <PasswordPopup @passwordHandler="passwordHandler" />
    <LeavePopup @leaveHandler="leaveHandler" />
    <InfoPopup />
    <EmailVerificationPopup />
    <MessagePopup />
    <ConfirmPopup />
    <OpenProjectPopup />
    <SaveProjectPopup />
    <RenameProjectPopup />
    <UrlEditProjectPopup />
    <ProjectDetailPopup />
    <AddDevicePopup />
    <HelpPopup />
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import Header from '@/components/Header.vue'
import SideBar from '@/components/SideBar.vue'
import BlockArea from '@/components/BlockArea.vue'
import CodeArea from '@/components/CodeArea.vue'
import Login from '@/components/Login.vue'
import LanguagePopup from '@/components/popup/LanguagePopup.vue'
import UserPopup from '@/components/popup/UserPopup.vue'
import PasswordPopup from '@/components/popup/PasswordPopup.vue'
import LeavePopup from '@/components/popup/LeavePopup.vue'
import InfoPopup from '@/components/popup/InfoPopup.vue'
import EmailVerificationPopup from '@/components/popup/EmailVerificationPopup.vue'
import MessagePopup from '@/components/popup/MessagePopup.vue'
import ConfirmPopup from '@/components/popup/ConfirmPopup.vue'
import OpenProjectPopup from '@/components/popup/OpenProjectPopup.vue'
import SaveProjectPopup from '@/components/popup/SaveProjectPopup.vue'
import RenameProjectPopup from '@/components/popup/RenameProjectPopup.vue'
import UrlEditProjectPopup from '@/components/popup/UrlEditProjectPopup.vue'
import ProjectDetailPopup from '@/components/popup/ProjectDetailPopup.vue'
import AddDevicePopup from '@/components/popup/AddDevicePopup.vue'
import HelpPopup from '@/components/popup/HelpPopup.vue'
import storage from '@/libs/storage'
import ui from '@/libs/ui'
import { firebaseAuth } from '@/libs/firebase'
import {
  project as projectController,
  device as deviceController,
  tab as tabController
} from '@/controller'
import blocklyUtil from '@/blockly/blocklyUtil'

export default {
  name: 'App',
  components: {
    Header,
    SideBar,
    BlockArea,
    CodeArea,
    Login,
    LanguagePopup,
    UserPopup,
    PasswordPopup,
    LeavePopup,
    InfoPopup,
    EmailVerificationPopup,
    MessagePopup,
    ConfirmPopup,
    OpenProjectPopup,
    SaveProjectPopup,
    RenameProjectPopup,
    UrlEditProjectPopup,
    ProjectDetailPopup,
    AddDevicePopup,
    HelpPopup
  },
  data() {
    return {
      tryRetchUserCount: 0
    }
  },
  computed: {
    ...mapState([
      'language',
      'userInfo',
      'project',
      'showSideBar',
      'isModified',
      'isPublish'
    ])
  },
  created() {
    this.onResize()
    firebaseAuth.initializeApp()
    let userLang =
      storage.local.getItem('language') ||
      navigator.language ||
      navigator.userLanguage
    this.setLanguageCallback(this.languageHandler)
    const language = userLang.substr(0, 2)
    if (this.language !== language) i18n.locale = language
    this.setLanguage(language)
    const showSideBar = storage.session.getItem('showSideBar')
    const isShow = showSideBar ? showSideBar === 'true' : true
    this.setShowSideBar(isShow)
    window.addEventListener('beforeunload', this.onBeforeUnload)
  },
  mounted() {
    this.preloadInitData()
    firebaseAuth.initAuth(this.language, {
      loginSuccess: this.loginSuccessHandler,
      loginFail: this.loginFailHandler,
      loginNewUser: this.loginNewUserHandler
    })
    this.fetchCurrentUser()
    window.addEventListener('resize', this.onResize)
  },
  componentWillUnmount() {
    firebaseAuth.removeAuth()
  },
  methods: {
    ...mapMutations([
      'setPhoneMode',
      'setDisplayWidth',
      'setLanguage',
      'setLanguageCallback',
      'setLoginData',
      'setUserInfo',
      'setIsAdmin',
      'setIsPublish',
      'setBlocklyXml',
      'setShowSideBar',
      'setDevices',
      'setMyDevices',
      'setTabs',
      'setDevice',
      'setProject',
      'setPasswordPopupData',
      'setLeavePopupData',
      'setEmailVerificationPopupData',
      'setMessagePopupData'
    ]),
    async fetchCurrentUser() {
      log('App.vue fetchCurrentUser()')
      const authResult = firebaseAuth.getUser()
      if (authResult) {
        const userInfo = _.merge(
          _.pick(authResult, ['uid', 'displayName', 'email']),
          _.pick(authResult.providerData[0], 'providerId')
        )
        await this.getInitData()
        await this.setUserInfoProcess(userInfo)
      } else {
        if (this.tryRetchUserCount++ < 10)
          setTimeout(this.fetchCurrentUser, 200)
        else await this.getInitData()
      }
    },
    async loginSuccessHandler(authResult) {
      // log(JSON.stringify(authResult))
      const userInfo = _.merge(
        _.pick(authResult.user, ['uid', 'displayName', 'email']),
        _.pick(authResult.additionalUserInfo, 'providerId')
      )
      await this.getInitData()
      await this.setUserInfoProcess(userInfo)
      ui.makeToast(this, this.$t('Login Success!'), 'success')
    },
    preloadInitData() {
      log('App.vue preloadInitData()')
      this.initDevices()
      const tabs = storage.local.getItem('tabs')
      if (tabs) this.setTabs(JSON.parse(tabs))
    },
    initDevices() {
      const devicesString = storage.local.getItem('devices')
      let myDevices = storage.local.getItem('my_devices')
      const type = storage.session.getItem('device')
      if (devicesString) {
        const devices = JSON.parse(devicesString)
        this.setDevices(devices)
        if (myDevices) myDevices = JSON.parse(myDevices)
        else myDevices = _.filter(devices, { type: 'ifline' })
        if (type) this.setDevice(_.find(myDevices, { type: type }))
        else this.setDevice(myDevices[0])
        this.setMyDevices(myDevices)
      }
    },
    async getInitData() {
      log('App.vue getInitData()')
      this.isGuest = await firebaseAuth.checkLogin()
      await deviceController.get(this)
      await tabController.get(this)
      if (this.isGuest) {
        firebaseAuth.logout()
      }
      this.initDevices()
    },
    async setUserInfoProcess(userInfo) {
      log('App.vue setUserInfoProcess()')
      const xml = await projectController.setUserAndProject(this, userInfo)
      let currentJson = JSON.parse(storage.local.getItem('current'))
      currentJson = currentJson ? currentJson : {}
      const uid = _.get(this.userInfo, 'uid')
      const fileName = this.project.fileName
      if (currentJson.uid !== uid) {
        if (xml) {
          this.setBlocklyXml(xml)
          blocklyUtil.setWorkspaceXml(xml)
        }
        const fileName = this.project.fileName
        const type = this.project.type
        const currentJson = { uid, fileName, type }
        storage.local.setItem('current', JSON.stringify(currentJson))
      } else if (
        currentJson.fileName !== fileName ||
        currentJson.type !== this.project.type
      ) {
        if (xml) {
          this.setBlocklyXml(xml)
          blocklyUtil.setWorkspaceXml(xml)
        }
      }
    },
    loginFailHandler(error, emailVerify = false, user = undefined) {
      if (emailVerify) {
        this.setLoginData({
          isShow: false
        })
        if (user)
          this.setEmailVerificationPopupData({
            title: this.$t('Login Email verification'),
            message: this.$t(error),
            user: user,
            isShow: true
          })
        else
          this.setMessagePopupData({
            title: this.$t('Login Email verification'),
            message: this.$t(error),
            isShow: true
          })
      } else {
        log.error(error)
        ui.makeToast(this, this.$t('Login Fail!'), 'danger')
      }
    },
    async loginNewUserHandler(uid, email) {
      log('App.vue loginNewUserHandler()')
      const isGuest = await firebaseAuth.checkLogin()
      await projectController.setGroup(uid, email)
      if (isGuest) {
        firebaseAuth.logout()
      }
    },
    passwordHandler(password) {
      firebaseAuth.updatePassword(
        password,
        this.passwordSuccess,
        this.commonError
      )
    },
    passwordSuccess() {
      this.setPasswordPopupData({
        isShow: false
      })
      ui.makeToast(this, this.$t('Password Save Success!'), 'success')
    },
    commonError(errorMessage) {
      log.error(errorMessage)
      ui.makeToast(this, errorMessage, 'danger', 5000)
    },
    async leaveHandler() {
      await projectController.removeAll(this.userInfo)
      firebaseAuth.deleteUser(this.leaveSuccess, this.commonError)
    },
    leaveSuccess() {
      this.setLeavePopupData({
        isShow: false
      })
      this.setUserInfo({})
      this.setProject({})
      storage.session.removeItem('device')
      storage.session.removeItem('showSideBar')
      storage.session.removeItem('userInfo')
      ui.makeToast(this, this.$t('Leave iFBlock Success!'), 'success')
    },
    languageHandler(language) {
      storage.local.setItem('language', language)
      location.reload()
    },
    onResize() {
      const width = window.innerWidth
      this.setPhoneMode(width <= 680)
      this.setDisplayWidth(width)
    },
    onBeforeUnload(event) {
      log('onBeforeUnload()')
      if (this.isPublish) {
        this.setIsPublish(false)
      } else {
        log(this.isModified)
        if (this.isModified) {
          const dialogText = ''
          event.returnValue = dialogText
          return dialogText
        }
      }
    }
  }
}
</script>

<style>
html,
body {
  display: block;
  margin: 0;
  width: 100%;
  height: 100%;
  overflow-y: hidden;
}

#app {
  font-family: NanumSquare, Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  width: 100%;
  height: 100%;
}

.centered {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.workspace-area {
  display: inline-block;
  margin-top: 0;
  margin-left: 0;
  width: 100%;
  height: calc(100% - 56px);
  vertical-align: top;
  transition: 0.5s;
}

.showSideBar .workspace-area {
  margin-left: 275px;
  width: calc(100% - 275px);
}

/* ui.makeToast */
div.b-toaster.b-toaster-top-center .b-toaster-slot,
div.b-toaster.b-toaster-top-full .b-toaster-slot,
div.b-toaster.b-toaster-top-left .b-toaster-slot,
div.b-toaster.b-toaster-top-right .b-toaster-slot {
  top: 65px;
}

div.b-toaster .b-toaster-slot .b-toast-success .toast {
  background-color: #34d0ff;
}

div.b-toaster .b-toaster-slot .b-toast-success .toast-header {
  color: #1540c0;
  background-color: #cae9fa;
  border-bottom: none;
}

div.b-toaster .b-toaster-slot .b-toast-success .toast-header button.close {
  color: #1540c0;
  opacity: 1;
}

div.b-toaster .b-toaster-slot .b-toast-danger .toast {
  background-color: #ff8688;
}

div.b-toaster .b-toaster-slot .b-toast-danger .toast-header {
  color: #9b1c24;
  background-color: #ffe1cd;
  border-bottom: none;
}

div.b-toaster .b-toaster-slot .b-toast-danger .toast-header button.close {
  color: #9b1c24;
  opacity: 1;
}

div.b-toast .toast .toast-body {
  display: none;
}
</style>
